"use client";
/**
 * Tilbud / Offers — Svendetid marketing feature page.
 *
 * Built on the shared svendetid-ds design system, mirroring the invoice-scanning,
 * reports, calendar and quality-control pages: a live mockup in the hero, a
 * "without / with" comparison, three action-oriented feature blocks with mini
 * mockups, a dark AI block, a feature grid, a platform grid and a FAQ.
 *
 * The flagship story — what makes Svendetid unique: the tradesperson sends a
 * single link to the end customer. The customer fills out what they need and
 * when they want the work done. From that, the AI finds the best time, writes
 * the job description, lists the materials needed and estimates the cost — so a
 * finished, send-ready offer is built automatically. One click, and the offer
 * is done.
 *
 * Language-parametrized so /features/offer (da) and /en/features/offer (en)
 * share one source.
 */
import { useState } from "react";
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    eyebrow: "Automatiske tilbud i Svendetid",
    h1a: "Send et link — ",
    h1b: "få tilbuddet bygget",
    h1c: " for dig",
    sub: "Send ét link til kunden. De udfylder, hvad de skal bruge, og hvornår det skal laves. Så finder AI'en den bedste tid, beskriver opgaven, og giver dig materialeliste og prisoverslag. Tilbuddet er klar — du skal bare trykke send.",
    cta: "Kom i gang – det er gratis",
    seePricing: "Se priser",

    trust2: "Gratis at komme i gang",
    trust3: "Dansk support",
    // hero mockup labels
    heroLinkLabel: "Kundens link",
    heroFormTitle: "Hvad skal laves?",
    heroFormLines: [
      "Udskiftning af køkkenvask",
      "Adresse: Nørregade 12",
      "Helst i næste uge",
    ],
    heroBuiltLabel: "Tilbud bygget af AI",
    heroOfferTitle: "Tilbud · Køkkenvask",
    heroOfferTime: "Tor. 26. juni · kl. 08–11",
    heroLines: [
      { name: "Køkkenarmatur, blandingsbatteri", qty: "1 stk", price: "kr 899" },
      { name: "Vandlås + tilslutningssæt", qty: "1 sæt", price: "kr 245" },
      { name: "Arbejdsløn (3 t)", qty: "3 t", price: "kr 1.350" },
    ],
    heroTotalLabel: "I alt inkl. moms",
    heroTotal: "kr 3.118",
    heroSend: "Klar til at sende",
    // without / with
    cmpH2: "Fra forespørgsel til tilbud — uden arbejdet",
    withoutLabel: "Uden Svendetid",
    withLabel: "Med Svendetid",
    without: [
      "Ringe frem og tilbage for at finde ud af, hvad opgaven er",
      "Sidde om aftenen og skrive tilbud op i hånden",
      "Gætte på materialer og priser — og ramme ved siden af",
      "Lede i kalenderen efter en tid, der passer begge",
    ],
    with: [
      "Kunden udfylder selv opgaven via et link, du sender",
      "AI'en skriver tilbuddet — beskrivelse, tid og priser",
      "Materialeliste og prisoverslag genereret automatisk",
      "Bedste ledige tid foreslået ud fra din kalender",
    ],
    // section intro
    introLabel: "Sådan virker det",
    introH2: "Fra link til færdigt tilbud i tre trin",
    introSub: "Du sender. Kunden udfylder. AI'en bygger.",
    // three feature blocks
    blocks: [
      {
        label: "Del et link",
        h3: "Send kunden et link — de udfylder selv resten",
        body: "I stedet for at ringe frem og tilbage sender du et enkelt link til kunden. De beskriver, hvad der skal laves, hvor det er, og hvornår det passer dem. Du behøver ikke være til stede — informationen er samlet, inden du overhovedet løfter telefonen.",
        chips: [
          { icon: "link-outline", text: "Ét link" },
          { icon: "paper-plane-outline", text: "Send på SMS eller mail" },
          { icon: "create-outline", text: "Kunden udfylder" },
        ],
        mock: "link",
      },
      {
        label: "AI bygger tilbuddet",
        h3: "Opgave, materialer og pris — skrevet automatisk",
        body: "Ud fra kundens svar skriver AI'en hele tilbuddet: en klar beskrivelse af opgaven, en liste over de materialer der skal bruges, og et prisoverslag med arbejdsløn oven i. Du får et færdigt udkast — ikke et tomt ark.",
        chips: [
          { icon: "color-wand-outline", text: "Auto-beskrivelse" },
          { icon: "list-outline", text: "Materialeliste" },
          { icon: "calculator-outline", text: "Prisoverslag" },
        ],
        mock: "build",
      },
      {
        label: "Bedste tid foreslået",
        h3: "AI'en finder en tid — og tilbuddet er klar til at sende",
        body: "AI'en kigger på din kalender og kundens ønsker og foreslår den bedste ledige tid. Tjek udkastet, ret om nødvendigt, og send. Ét klik, og kunden har et professionelt tilbud med tid, opgave og pris — uden at du har skrevet en linje.",
        chips: [
          { icon: "calendar-outline", text: "Bedste tid" },
          { icon: "checkmark-circle", text: "Tjek & ret" },
          { icon: "paper-plane-outline", text: "Send med ét klik" },
        ],
        mock: "schedule",
      },
    ],
    // link mock labels
    mockLinkTitle: "Forespørgsel",
    mockLinkSub: "svendetid.dk/tilbud/3f9a",
    mockLinkField1: "Opgave",
    mockLinkVal1: "Udskiftning af køkkenvask",
    mockLinkField2: "Adresse",
    mockLinkVal2: "Nørregade 12, 8000",
    mockLinkField3: "Hvornår",
    mockLinkVal3: "Helst i næste uge",
    mockLinkSubmit: "Send forespørgsel",
    // build mock labels
    mockBuildTitle: "Genereret tilbud",
    mockColItem: "Linje",
    mockColQty: "Antal",
    mockColPrice: "Pris",
    mockBuildBy: "Skrevet af AI",
    // schedule mock labels
    mockSchedTitle: "Foreslået tid",
    mockSchedDay: "Torsdag 26. juni",
    mockSchedTime: "08:00 – 11:00",
    mockSchedNote: "Passer din kalender og kundens ønske",
    mockSchedSend: "Send tilbud",
    // AI block
    aiLabel: "AI-drevne tilbud",
    aiH2a: "Svendetid AI skriver ",
    aiH2b: "tilbuddet for dig",
    aiBody: "Spørg om hvad som helst: »Lav et tilbud på udskiftning af køkkenvask« eller »Hvilke tilbud er sendt men ikke besvaret?«. AI'en bygger tilbuddet, holder styr på de udestående og minder dig om at følge op.",
    aiCta: "Prøv Svend AI",
    aiQuestion: "Lav et tilbud ud fra Hansens forespørgsel om køkkenvask",
    aiAnswerIntro: "Tilbud bygget — klar til at sende:",
    aiRow1: "Materialer · 3 linjer",
    aiRow1Val: "kr 1.144",
    aiRow2: "Arbejdsløn · 3 t",
    aiRow2Val: "kr 1.350",
    aiAnswerOutro: "Foreslået tid: tor. 26. juni kl. 08–11. Vil du sende det til Hansen nu?",
    // feature grid
    gridH2: "Plus alt det, du skal bruge for at vinde opgaven",
    grid: [
      { icon: "link-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Forespørgselslink", body: "Send kunden et link, så de selv udfylder opgaven. Al information samlet ét sted, inden du går i gang." },
      { icon: "color-wand-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "AI-genereret tilbud", body: "Opgavebeskrivelse, materialeliste og prisoverslag skrevet automatisk ud fra kundens svar." },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Bedste tid foreslået", body: "AI'en finder en ledig tid, der passer din kalender og kundens ønske — uden manuel kabale." },
      { icon: "create-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Skabeloner & tilpasning", body: "Start fra en skabelon eller ret AI'ens udkast. Dine priser, dine vilkår, dit logo." },
      { icon: "swap-horizontal-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Tilbud → faktura", body: "Når kunden siger ja, bliver tilbuddet til et job og en faktura med ét klik — uden at taste det hele om." },
      { icon: "eye-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Følg op automatisk", body: "Se hvilke tilbud der er sendt, åbnet og accepteret — og bliv mindet om at følge op på dem, der mangler svar." },
    ],
    // platform
    platLabel: "Svendetid-platformen",
    platH2: "Tilbud er kun begyndelsen",
    platSub: "Tilbud er en del af platformen — jobs, tid, kunder, fakturascanning og fakturering i ét samlet system. Alt taler sammen.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Styr hvert job", href: "/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Fakturascanning", sub: "Materialer på jobbet", href: "/features/invoice-scanning" },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Kalender", sub: "Planlæg sjakket", href: "/features/calendar" },
      { icon: "bar-chart-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Rapporter", sub: "Overblik der virker", href: "/features/reports" },
      { icon: "camera-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Kvalitetssikring", sub: "Fotos & underskrift", href: "/features/quality-control" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "Alle funktioner", sub: "Se hele platformen", href: "/features" },
    ],
    // security
    secLabel: "Sikkerhed hele vejen",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data i EU" },
      { icon: "chatbubbles-outline", text: "Support på dansk" },
    ],
    // FAQ
    faqH2: "Ofte stillede spørgsmål",
    faqs: [
      { q: "Hvordan får kunden adgang til at udfylde?", a: "Du sender et link på SMS eller mail. Kunden åbner det i browseren — ingen app eller login. De beskriver opgaven, adressen og hvornår det passer dem, og forespørgslen lander hos dig." },
      { q: "Hvad bygger AI'en helt præcist?", a: "Ud fra kundens svar skriver AI'en en opgavebeskrivelse, en materialeliste med antal, et prisoverslag med arbejdsløn — og foreslår den bedste ledige tid ud fra din kalender. Du får et færdigt udkast." },
      { q: "Kan jeg rette i tilbuddet, før det sendes?", a: "Ja. AI'ens udkast er et udgangspunkt. Du kan rette beskrivelse, linjer, priser og tid med det samme. Dine priser og vilkår styrer — AI'en gør grovarbejdet." },
      { q: "Hvor kommer materialer og priser fra?", a: "AI'en foreslår materialer ud fra opgaven og bruger dine priser, hvor de findes. Du kan altid tilføje, fjerne eller justere linjer, før tilbuddet er klar til at sende." },
      { q: "Hvad sker der, når kunden siger ja?", a: "Et accepteret tilbud bliver til et job i kalenderen og kan konverteres til en faktura med ét klik — uden at du skal taste det hele om igen." },
      { q: "Hvad er gratis, og hvad koster penge?", a: "Du kan komme i gang gratis og prøve tilbud. De automatiske AI-tilbud er en del af de betalte planer — opgradér når du er klar." },
    ],
    // CTA band
    bandH2: "Send et link — og få tilbuddet bygget for dig",
    bandBody: "Slut dig til håndværkere der lader AI'en skrive tilbuddet. Intet betalingskort. Gratis at komme i gang.",
  },
  en: {
    eyebrow: "Automatic offers in Svendetid",
    h1a: "Send a link — ",
    h1b: "get the offer built",
    h1c: " for you",
    sub: "Send the customer one link. They fill in what they need and when they want it done. Then the AI finds the best time, describes the job, and gives you a materials list and a cost estimate. The offer is ready — you just hit send.",
    cta: "Get started – it's free",
    seePricing: "See pricing",

    trust2: "Free to get started",
    trust3: "Danish support",
    heroLinkLabel: "Customer's link",
    heroFormTitle: "What needs doing?",
    heroFormLines: [
      "Replace kitchen sink",
      "Address: Nørregade 12",
      "Ideally next week",
    ],
    heroBuiltLabel: "Offer built by AI",
    heroOfferTitle: "Offer · Kitchen sink",
    heroOfferTime: "Thu 26 Jun · 08–11",
    heroLines: [
      { name: "Kitchen mixer tap", qty: "1 pc", price: "kr 899" },
      { name: "Trap + connection kit", qty: "1 set", price: "kr 245" },
      { name: "Labour (3 h)", qty: "3 h", price: "kr 1,350" },
    ],
    heroTotalLabel: "Total incl. VAT",
    heroTotal: "kr 3,118",
    heroSend: "Ready to send",
    cmpH2: "From request to offer — without the work",
    withoutLabel: "Without Svendetid",
    withLabel: "With Svendetid",
    without: [
      "Calling back and forth to work out what the job even is",
      "Sitting up at night writing the offer by hand",
      "Guessing at materials and prices — and getting it wrong",
      "Hunting the calendar for a time that suits you both",
    ],
    with: [
      "The customer fills in the job themselves via a link you send",
      "The AI writes the offer — description, time and prices",
      "Materials list and cost estimate generated automatically",
      "Best available time suggested from your calendar",
    ],
    introLabel: "How it works",
    introH2: "From link to finished offer in three steps",
    introSub: "You send. The customer fills in. The AI builds.",
    blocks: [
      {
        label: "Share a link",
        h3: "Send the customer a link — they fill in the rest",
        body: "Instead of calling back and forth, you send the customer a single link. They describe what needs doing, where it is, and when it suits them. You don't have to be there — the information is gathered before you even pick up the phone.",
        chips: [
          { icon: "link-outline", text: "One link" },
          { icon: "paper-plane-outline", text: "Send by SMS or email" },
          { icon: "create-outline", text: "Customer fills in" },
        ],
        mock: "link",
      },
      {
        label: "AI builds the offer",
        h3: "Job, materials and price — written automatically",
        body: "From the customer's answers the AI writes the whole offer: a clear description of the job, a list of the materials needed, and a cost estimate with labour on top. You get a finished draft — not a blank page.",
        chips: [
          { icon: "color-wand-outline", text: "Auto description" },
          { icon: "list-outline", text: "Materials list" },
          { icon: "calculator-outline", text: "Cost estimate" },
        ],
        mock: "build",
      },
      {
        label: "Best time suggested",
        h3: "The AI finds a time — and the offer is ready to send",
        body: "The AI looks at your calendar and the customer's wishes and suggests the best available time. Check the draft, edit if needed, and send. One click, and the customer has a professional offer with time, job and price — without you writing a single line.",
        chips: [
          { icon: "calendar-outline", text: "Best time" },
          { icon: "checkmark-circle", text: "Check & edit" },
          { icon: "paper-plane-outline", text: "Send in one click" },
        ],
        mock: "schedule",
      },
    ],
    mockLinkTitle: "Request",
    mockLinkSub: "svendetid.dk/offer/3f9a",
    mockLinkField1: "Job",
    mockLinkVal1: "Replace kitchen sink",
    mockLinkField2: "Address",
    mockLinkVal2: "Nørregade 12, 8000",
    mockLinkField3: "When",
    mockLinkVal3: "Ideally next week",
    mockLinkSubmit: "Send request",
    mockBuildTitle: "Generated offer",
    mockColItem: "Line",
    mockColQty: "Qty",
    mockColPrice: "Price",
    mockBuildBy: "Written by AI",
    mockSchedTitle: "Suggested time",
    mockSchedDay: "Thursday 26 June",
    mockSchedTime: "08:00 – 11:00",
    mockSchedNote: "Fits your calendar and the customer's wish",
    mockSchedSend: "Send offer",
    aiLabel: "AI-driven offers",
    aiH2a: "Svendetid AI writes ",
    aiH2b: "the offer for you",
    aiBody: "Ask anything: “Make an offer for replacing a kitchen sink” or “Which offers are sent but not answered?”. The AI builds the offer, keeps track of the outstanding ones and reminds you to follow up.",
    aiCta: "Try Svendetid AI",
    aiQuestion: "Build an offer from Hansen's kitchen sink request",
    aiAnswerIntro: "Offer built — ready to send:",
    aiRow1: "Materials · 3 lines",
    aiRow1Val: "kr 1,144",
    aiRow2: "Labour · 3 h",
    aiRow2Val: "kr 1,350",
    aiAnswerOutro: "Suggested time: Thu 26 Jun, 08–11. Want to send it to Hansen now?",
    gridH2: "Plus everything you need to win the job",
    grid: [
      { icon: "link-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Request link", body: "Send the customer a link so they fill in the job themselves. All the information in one place before you start." },
      { icon: "color-wand-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "AI-generated offer", body: "Job description, materials list and cost estimate written automatically from the customer's answers." },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Best time suggested", body: "The AI finds an available time that fits your calendar and the customer's wish — no manual juggling." },
      { icon: "create-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Templates & tweaks", body: "Start from a template or edit the AI's draft. Your prices, your terms, your logo." },
      { icon: "swap-horizontal-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Offer → invoice", body: "When the customer says yes, the offer becomes a job and an invoice in one click — without retyping everything." },
      { icon: "eye-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Follow up automatically", body: "See which offers are sent, opened and accepted — and get reminded to follow up on the ones still waiting." },
    ],
    platLabel: "The Svendetid platform",
    platH2: "Offers are only the beginning",
    platSub: "Offers are part of the platform — jobs, time, customers, invoice scanning and invoicing in one connected system. Everything talks to everything.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Run every job", href: "/en/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Invoice scanning", sub: "Materials on the job", href: "/en/features/invoice-scanning" },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Calendar", sub: "Plan the crew", href: "/en/features/calendar" },
      { icon: "bar-chart-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Reports", sub: "Overview that works", href: "/en/features/reports" },
      { icon: "camera-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Quality control", sub: "Photos & signature", href: "/en/features/quality-control" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "All features", sub: "See the whole platform", href: "/en/features" },
    ],
    secLabel: "Security all the way",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data in the EU" },
      { icon: "chatbubbles-outline", text: "Support in Danish" },
    ],
    faqH2: "Frequently asked questions",
    faqs: [
      { q: "How does the customer get access to fill it in?", a: "You send a link by SMS or email. The customer opens it in the browser — no app or login. They describe the job, the address and when it suits them, and the request lands with you." },
      { q: "What exactly does the AI build?", a: "From the customer's answers the AI writes a job description, a materials list with quantities, a cost estimate with labour — and suggests the best available time from your calendar. You get a finished draft." },
      { q: "Can I edit the offer before it's sent?", a: "Yes. The AI's draft is a starting point. You can edit the description, lines, prices and time straight away. Your prices and terms rule — the AI does the grunt work." },
      { q: "Where do the materials and prices come from?", a: "The AI suggests materials based on the job and uses your prices where they exist. You can always add, remove or adjust lines before the offer is ready to send." },
      { q: "What happens when the customer says yes?", a: "An accepted offer becomes a job in the calendar and can be converted to an invoice in one click — without retyping everything again." },
      { q: "What's free and what costs money?", a: "You can get started free and try offers. The automatic AI offers are part of the paid plans — upgrade when you're ready." },
    ],
    bandH2: "Send a link — and get the offer built for you",
    bandBody: "Join trades businesses that let the AI write the offer. No payment card. Free to get started.",
  },
} as const;

type TT = (typeof T)["da"] | (typeof T)["en"];

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
  padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column", gap: 15,
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em",
  textTransform: "uppercase", color: "var(--accent-strong)",
};

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14 }}>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      <span style={monoLabel}>{label}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

type Card = { icon: string; iconBg: string; iconColor: string; title: string; body: string };

function FeatureCard({ icon, iconBg, iconColor, title, body }: Card) {
  return (
    <div style={cardStyle}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={24} color={iconColor} />
      </div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{body}</p>
    </div>
  );
}

/* ------------------------------- Hero mockup ----------------------------- */
/* Customer's filled-in link on the left → AI-built offer on the right.      */
function HeroMock({ t }: { t: TT }) {
  return (
    <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "0.85fr 1.15fr", gap: 20, alignItems: "stretch", textAlign: "left" }}>
      {/* Customer's link form */}
      <div style={{ position: "relative", background: "#FBFAF6", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 16px 36px rgba(16,33,27,0.14)", padding: "18px 16px", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", left: 0, right: 0, top: 0, height: 3, background: "linear-gradient(90deg,transparent,var(--brand),transparent)", boxShadow: "0 0 14px var(--brand)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ width: 26, height: 26, borderRadius: 8, background: "var(--green-100)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="link-outline" size={15} color="var(--brand)" /></span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.heroLinkLabel}</span>
        </div>
        <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 15, color: "var(--green-900)" }}>{t.heroFormTitle}</div>
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 9 }}>
          {t.heroFormLines.map((line) => (
            <div key={line} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#fff", border: "1px solid var(--hairline)", borderRadius: 9 }}>
              <Icon name="create-outline" size={13} color="var(--text-muted)" />
              <span style={{ fontSize: 12, color: "var(--green-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{line}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--brand)", borderRadius: 9, padding: "9px 12px" }}>
          <Icon name="paper-plane-outline" size={13} color="#fff" />{t.mockLinkSubmit}
        </div>
      </div>

      {/* AI-built offer */}
      <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 16px 36px rgba(16,33,27,0.14)", padding: 18, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ width: 26, height: 26, borderRadius: 8, background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="color-wand-outline" size={15} color="var(--accent-strong)" /></span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--accent-strong)" }}>{t.heroBuiltLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 15, color: "var(--green-900)" }}>{t.heroOfferTitle}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "#2056C0", background: "#E3ECFB", borderRadius: 8, padding: "4px 9px" }}><Icon name="calendar-outline" size={12} color="#2056C0" />{t.heroOfferTime}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {t.heroLines.map((l) => (
            <div key={l.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", background: "var(--green-50)", border: "1px solid var(--hairline)", borderRadius: 10 }}>
              <Icon name="checkmark-circle" size={15} color="var(--brand)" />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--green-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</span>
              <span style={{ fontSize: 12, color: "var(--text-muted)", flex: "none" }}>{l.qty}</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600, color: "var(--green-900)", flex: "none", fontVariantNumeric: "tabular-nums" }}>{l.price}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{t.heroTotalLabel}</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 15, fontWeight: 700, color: "var(--green-900)", fontVariantNumeric: "tabular-nums" }}>{t.heroTotal}</span>
        </div>
        <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 7, alignSelf: "flex-start", fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--brand)", borderRadius: 9, padding: "8px 13px", boxShadow: "0 8px 18px rgba(47,107,72,0.28)" }}>
          <Icon name="paper-plane-outline" size={14} color="#fff" />{t.heroSend}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Feature-block mocks ------------------------- */
function LinkMock({ t }: { t: TT }) {
  const fields = [
    { label: t.mockLinkField1, val: t.mockLinkVal1 },
    { label: t.mockLinkField2, val: t.mockLinkVal2 },
    { label: t.mockLinkField3, val: t.mockLinkVal3 },
  ];
  return (
    <div style={{ width: "100%", maxWidth: 320, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 18, position: "relative", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ width: 28, height: 28, borderRadius: 8, background: "var(--green-100)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="link-outline" size={15} color="var(--brand)" /></span>
        <span style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 14, color: "var(--green-900)" }}>{t.mockLinkTitle}</span>
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)", marginBottom: 12, paddingLeft: 36 }}>{t.mockLinkSub}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {fields.map((f) => (
          <div key={f.label}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>{f.label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--green-900)", padding: "9px 11px", background: "var(--green-50)", border: "1px solid var(--hairline)", borderRadius: 9 }}>{f.val}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--brand)", borderRadius: 10, padding: "10px 14px" }}>
        <Icon name="paper-plane-outline" size={14} color="#fff" />{t.mockLinkSubmit}
      </div>
    </div>
  );
}

function BuildMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 18 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 12 }}>{t.mockBuildTitle}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "0 12px", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", paddingBottom: 8, borderBottom: "1px solid var(--hairline)" }}>
        <span>{t.mockColItem}</span><span style={{ textAlign: "right" }}>{t.mockColQty}</span><span style={{ textAlign: "right" }}>{t.mockColPrice}</span>
      </div>
      {t.heroLines.map((l) => (
        <div key={l.name} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "0 12px", fontSize: 13, padding: "10px 0", borderBottom: "1px solid var(--hairline)", alignItems: "center" }}>
          <span style={{ fontWeight: 600, color: "var(--green-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</span>
          <span style={{ textAlign: "right", color: "var(--text-muted)" }}>{l.qty}</span>
          <span style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--green-900)", fontVariantNumeric: "tabular-nums" }}>{l.price}</span>
        </div>
      ))}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: "var(--accent-strong)" }}>
          <Icon name="color-wand-outline" size={14} color="var(--accent-strong)" />{t.mockBuildBy}
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--green-900)" }}>{t.heroTotal}</span>
      </div>
    </div>
  );
}

function ScheduleMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", maxWidth: 320, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 20 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 14 }}>{t.mockSchedTitle}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 14px", background: "#E3ECFB", border: "1px solid #C9DAF6", borderRadius: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="calendar-outline" size={20} color="#2056C0" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--green-900)" }}>{t.mockSchedDay}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "#2056C0" }}>{t.mockSchedTime}</div>
        </div>
        <Icon name="checkmark-circle" size={20} color="var(--brand)" />
      </div>
      <div style={{ marginTop: 12, display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>
        <Icon name="sparkles" size={14} color="var(--accent-strong)" style={{ marginTop: 1, flex: "none" }} />
        <span>{t.mockSchedNote}</span>
      </div>
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--brand)", borderRadius: 10, padding: "10px 14px", boxShadow: "0 8px 18px rgba(47,107,72,0.28)" }}>
        <Icon name="paper-plane-outline" size={14} color="#fff" />{t.mockSchedSend}
      </div>
    </div>
  );
}

const MOCK_BG: Record<string, string> = {
  link: "linear-gradient(155deg,var(--green-50),#EEF6F0)",
  build: "linear-gradient(155deg,var(--orange-100),#FFF7EF)",
  schedule: "linear-gradient(155deg,#E3ECFB,#F2F6FD)",
};

function FeatureBlock({ block, reverse, t }: { block: TT["blocks"][number]; reverse: boolean; t: TT }) {
  const text = (
    <div style={{ order: reverse ? 2 : 1 }}>
      <div style={{ ...monoLabel, marginBottom: 14 }}>{block.label}</div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 32, lineHeight: 1.12, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{block.h3}</h3>
      <p style={{ fontSize: 17, color: "var(--text-muted)", lineHeight: 1.6, margin: "18px 0 0" }}>{block.body}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
        {block.chips.map((c) => (
          <span key={c.text} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 999, padding: "7px 13px" }}>
            <Icon name={c.icon} size={15} color="var(--brand)" />{c.text}
          </span>
        ))}
      </div>
    </div>
  );
  const visual = (
    <div style={{ order: reverse ? 1 : 2, background: MOCK_BG[block.mock], borderRadius: 28, padding: "clamp(28px,5%,48px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {block.mock === "link" && <LinkMock t={t} />}
      {block.mock === "build" && <BuildMock t={t} />}
      {block.mock === "schedule" && <ScheduleMock t={t} />}
    </div>
  );
  return (
    <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
      <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
        {text}
        {visual}
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "20px 22px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "var(--font-sans)" }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: "var(--green-900)" }}>{q}</span>
        <svg viewBox="0 0 24 24" width={18} height={18} fill="none" style={{ color: "var(--accent-strong)", flex: "none", transition: "transform .25s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div style={{ padding: "0 22px 22px", fontSize: 15, lineHeight: 1.6, color: "var(--text-muted)" }}>{a}</div>}
    </div>
  );
}

export default function OfferContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  const aiHref = lang === "en" ? "/en/ai" : "/ai";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`
        /* White canvas: when users zoom out (or anything overflows), the browser
           paints the area beyond the page with the html/body background — a dark
           value shows up as a black strip beside the white page. */
        html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}
        @media (max-width: 560px) {
          .svendetid-ds h1.ds-h1{font-size:34px !important;}
          .svendetid-ds h2{font-size:30px !important;}
          .svendetid-ds .ds-2col h3{font-size:26px !important;}
        }
      `}</style>

      <LandingNav lang={lang} accent="blue" />

      {/* ===== HERO ===== */}
      <section style={{ background: "#FFFFFF", color: "var(--text-body)", paddingTop: 64 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "72px 32px 40px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, ...monoLabel, marginBottom: 22 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />{t.eyebrow}
          </div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 58, lineHeight: 1.03, letterSpacing: "-0.025em", margin: "0 auto", maxWidth: "16ch", color: "var(--green-900)", textWrap: "balance" }}>
            {t.h1a}<span style={{ fontStyle: "italic", color: "var(--brand)" }}>{t.h1b}</span>{t.h1c}
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: "60ch", margin: "24px auto 0", textWrap: "pretty" }}>{t.sub}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 34 }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-soft" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "var(--green-900)", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid var(--border)", background: "var(--surface-card)" }}>{t.seePricing}</Link>
          </div>
          <div style={{ display: "flex", gap: 22, justifyContent: "center", alignItems: "center", flexWrap: "wrap", marginTop: 20, fontSize: 13, color: "var(--text-muted)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust2}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust3}</span>
          </div>

          {/* HERO MOCKUP — link → AI offer */}
          <div style={{ marginTop: 54, position: "relative" }}>
            <div
              style={{
                position: "relative",
                zIndex: 1,
                padding: "clamp(16px,3vw,32px)",
                borderRadius: 24,
                background: "linear-gradient(165deg, #EEF6F0 0%, #FFF7EF 55%, var(--green-100) 100%)",
                border: "1px solid var(--border)",
              }}
            >
              <HeroMock t={t} />
            </div>
          </div>
        </div>
      </section>

      {/* ===== WITHOUT / WITH ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "60ch", margin: "0 auto 40px" }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.08, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.cmpH2}</h2>
        </div>
        <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 20, padding: 30 }}>
            <div style={{ ...monoLabel, color: "var(--text-muted)", marginBottom: 18 }}>{t.withoutLabel}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.without.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--tint-rose)", borderRadius: "50%", padding: 3, color: "var(--danger)" }}><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: "var(--green-900)", border: "1px solid #2C3B35", borderRadius: 20, padding: 30, boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, ...monoLabel, color: "var(--accent)", marginBottom: 18 }}>
              <img src="/svendetid-logo.png" alt="" style={{ width: 18, height: 18 }} />{t.withLabel}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.with.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--brand)", borderRadius: "50%", padding: 3, color: "#fff" }}><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-on-hero)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION INTRO ===== */}
      <section style={{ maxWidth: 760, margin: "0 auto", padding: "40px 32px 8px", textAlign: "center" }}>
        <SectionEyebrow label={t.introLabel} />
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.introH2}</h2>
        <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0", maxWidth: "48ch" }}>{t.introSub}</p>
      </section>

      {/* ===== THREE FEATURE BLOCKS ===== */}
      {t.blocks.map((block, i) => (
        <FeatureBlock key={block.label} block={block} reverse={i === 1} t={t} />
      ))}

      {/* ===== AI BLOCK ===== */}
      <section style={{ padding: "64px 32px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(32px,5vw,64px)", boxShadow: "0 24px 60px rgba(16,33,27,0.22)", position: "relative", overflow: "hidden" }}>
          <div aria-hidden style={{ position: "absolute", top: "-30%", right: "-10%", width: 420, height: 420, background: "radial-gradient(circle, rgba(249,115,22,0.22), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center", position: "relative" }}>
            <div>
              <div style={{ ...monoLabel, color: "var(--accent)", marginBottom: 16 }}>{t.aiLabel}</div>
              <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--text-on-hero)" }}>{t.aiH2a}<span style={{ fontStyle: "italic", color: "#F4C8A6" }}>{t.aiH2b}</span></h2>
              <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", margin: "20px 0 0", maxWidth: "46ch" }}>{t.aiBody}</p>
              <div style={{ marginTop: 28 }}>
                <Link href={aiHref} className="btn-blue" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.aiCta}<Icon name="arrow-forward" size={18} /></Link>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid #2C3B35", borderRadius: 18, padding: 18, backdropFilter: "blur(4px)" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: "var(--text-on-hero)", background: "var(--brand)", borderRadius: "14px 14px 4px 14px", padding: "10px 14px", maxWidth: "80%" }}>{t.aiQuestion}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#FEC935,#FBA932)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="sparkles" size={15} color="#fff" /></span>
                <div style={{ background: "#151D1A", border: "1px solid #2C3B35", borderRadius: "4px 14px 14px 14px", padding: 14, fontSize: 13, color: "var(--text-on-hero)", lineHeight: 1.55 }}>
                  <div style={{ marginBottom: 10 }}>{t.aiAnswerIntro}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(47,107,72,0.18)", borderRadius: 8, marginBottom: 6 }}>
                    <span style={{ flex: 1 }}>{t.aiRow1}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#86D6B0" }}>{t.aiRow1Val}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(47,107,72,0.18)", borderRadius: 8 }}>
                    <span style={{ flex: 1 }}>{t.aiRow2}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#86D6B0" }}>{t.aiRow2Val}</span>
                  </div>
                  <div style={{ marginTop: 10, color: "var(--text-hero-muted)" }}>{t.aiAnswerOutro}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURE GRID ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.gridH2}</h2>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {t.grid.map((f) => <FeatureCard key={f.title} {...f} />)}
        </div>
      </section>

      {/* ===== PLATFORM ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "52ch", margin: "0 auto 36px" }}>
          <div style={{ ...monoLabel, marginBottom: 14 }}>{t.platLabel}</div>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.platH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0" }}>{t.platSub}</p>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {t.platform.map((p) => (
            <Link key={p.title} href={p.href} className="card-link" style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 16, textDecoration: "none", color: "inherit" }}>
              <span style={{ width: 38, height: 38, borderRadius: 10, background: p.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name={p.icon} size={19} color={p.iconColor} /></span>
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{p.title}</span>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{p.sub}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== SECURITY ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 32px 8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "18px 40px", padding: "28px 24px", background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 18 }}>
          <span style={{ ...monoLabel, color: "var(--text-muted)" }}>{t.secLabel}</span>
          {t.security.map((s) => (
            <span key={s.text} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--green-900)" }}><Icon name={s.icon} size={18} color="var(--brand)" />{s.text}</span>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section style={{ maxWidth: 780, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.faqH2}</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {t.faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 32px 72px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(40px,6vw,72px) 32px", textAlign: "center", position: "relative", overflow: "hidden", boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
          <div aria-hidden style={{ position: "absolute", bottom: "-40%", left: "50%", transform: "translateX(-50%)", width: 600, height: 400, background: "radial-gradient(circle, rgba(249,115,22,0.2), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <img src="/svendetid-logo.png" alt="" style={{ width: 48, height: 48, margin: "0 auto 22px", display: "block", position: "relative" }} />
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 44, lineHeight: 1.08, letterSpacing: "-0.02em", margin: "0 auto", maxWidth: "20ch", color: "#fff", position: "relative", textWrap: "balance" }}>{t.bandH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-hero-muted)", margin: "18px auto 0", maxWidth: "46ch", position: "relative" }}>{t.bandBody}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 30, position: "relative" }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/features/offer" enHref="/en/features/offer" />
    </div>
  );
}
